/* eslint-disable react/prop-types */
/* eslint-disable react/jsx-no-duplicate-props */

import React, { useRef } from 'react';
import { LoadingButton } from '@mui/lab';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import {Search} from 'lucide-react';


export const SectionCard = ({ step, title, subtitle, children }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-7 mb-5">
    <div className="flex items-start gap-3 mb-6">
      <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 text-xs font-bold text-gray-700">
        {step}
      </div>
      <div>
        <p className="text-sm font-bold text-gray-900 leading-snug">{title}</p>
        <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
      </div>
    </div>
    {children}
  </div>
);

export const FieldLabel = ({ children, required }) => (
  <p className="text-xs font-medium text-gray-700 mb-1.5">
    {children}
    {required && <span className="text-red-500 ml-0.5">*</span>}
  </p>
);

export const TierButton = ({ label, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-5 py-2 rounded-md text-xs font-semibold cursor-pointer transition-all duration-150 border
      ${selected
        ? 'bg-gray-900 text-white border-transparent'
        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
      }`}
  >
    {label}
  </button>
);

export const ScopeCard = ({ title, subtitle, selected, onClick, disabled }) => (
  <button
    type="button"
    onClick={!disabled ? onClick : undefined}
    disabled={disabled}
    className={`flex-1 p-4 rounded-lg transition-all duration-150 text-left border-none
      ${selected ? 'border-2 border-blue-600 bg-blue-50' : 'border border-gray-200 bg-white'}
      ${disabled ? 'cursor-default' : 'cursor-pointer hover:border-gray-400'}
    `}
  >
    <p className={`text-sm font-semibold ${selected ? 'text-blue-700' : 'text-gray-900'}`}>{title}</p>
    <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
  </button>
);

export const AssessmentTypeCard = ({ title, subtitle, selected, onClick, disabled }) => (
  <button
    type="button"
    onClick={!disabled ? onClick : undefined}
    disabled={disabled}
    className={`flex-1 px-4 py-3 rounded-lg transition-all duration-150 text-left border-none
      ${selected ? 'border-2 border-blue-600 bg-blue-50' : 'border border-gray-200 bg-white'}
      ${disabled && !selected ? 'opacity-60' : ''}
      ${disabled ? 'cursor-default' : 'cursor-pointer'}
    `}
  >
    <p className={`text-xs font-semibold ${selected ? 'text-blue-700' : 'text-gray-900'}`}>{title}</p>
    <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
  </button>
);

const STATUS_STYLES = {
  published: 'bg-green-100 text-green-800 border-green-300',
  archived:  'bg-red-100 text-red-700 border-red-300',
  draft:     'bg-yellow-100 text-yellow-700 border-yellow-300',
  deleted:   'bg-red-50 text-red-500 border-red-300',
};
const STATUS_LABELS = {
  published: 'Published',
  archived:  'Archived',
  draft:     'Draft',
  deleted:   'Deleted',
};
export const StatusChip = ({ status, isActive, isOnline }) => {
  let resolvedStatus = status;
  if (!resolvedStatus) {
    const active = isActive !== false && isActive !== 'false';
    const online = isOnline === true || isOnline === 'true';
    if (!active) resolvedStatus = 'archived';
    else if (online) resolvedStatus = 'published';
    else resolvedStatus = 'draft';
  }
  const chipStatus = STATUS_STYLES[resolvedStatus] ? resolvedStatus : 'draft';
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border flex-shrink-0 ${STATUS_STYLES[chipStatus]}`}>
      {STATUS_LABELS[chipStatus]}
    </span>
  );
};

export const ScopeChip = ({ label, color }) => {
  const colorMap = {
    blue:   'bg-blue-50 text-blue-700 border-blue-200',
    purple: 'bg-purple-50 text-purple-700 border-purple-200',
    green:  'bg-green-50 text-green-700 border-green-200',
  };
  const cls = colorMap[color] || colorMap.blue;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${cls}`}>
      {label}
    </span>
  );
};

ScopeChip.defaultProps = { color: 'blue' };

const resolveTierKey = (normalized) => {
  if (normalized.includes('PREMIUM')) return 'PREMIUM';
  if (normalized.includes('PRO')) return 'PRO';
  if (normalized.includes("FREE")) return 'FREE';
  return 'BASIC';
};

const TIER_CLASSES = {
  PREMIUM: 'bg-purple-50 text-purple-700 border-purple-400',
  PRO:     'bg-amber-50 text-amber-700 border-amber-400',
  BASIC:   'bg-gray-100 text-gray-600 border-gray-300',
  FREE:   'bg-gray-100 text-gray-600 border-gray-300',
};

export const TierBadge = ({ tier }) => {
  const normalized = (tier || '').toUpperCase();
  if (!normalized) return null;
  const key = resolveTierKey(normalized);
  return (
    <span className={`inline-flex items-center w-fit px-2 py-[2px] rounded-full text-[11px] font-semibold border ${TIER_CLASSES[key]}`}>
      {key}
    </span>
  );
};

export const FeatureBullets = ({ values, handleChange, push, remove }) => (
  <div className="flex flex-col gap-2 mb-4">
    {values.featureBullets.map((bullet, idx) => (
      // eslint-disable-next-line react/no-array-index-key
      <div key={idx} className="flex items-center gap-2">
        <DragIndicatorIcon style={{ fontSize: '18px', color: '#d1d5db', cursor: 'grab' }} />
        <div className="flex-1 relative">
          <input
            name={`featureBullets[${idx}]`}
            placeholder={`Feature ${idx + 1}`}
            value={bullet}
            onChange={handleChange}
            maxLength={80}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-16 text-sm text-gray-900 bg-white outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 placeholder-gray-400"
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none whitespace-nowrap">
            {bullet.length}/80
          </span>
        </div>
        <button
          type="button"
          onClick={() => remove(idx)}
          className="text-gray-400 hover:text-red-500 bg-transparent border-none p-1 cursor-pointer"
        >
          <DeleteOutlineIcon style={{ fontSize: '18px' }} />
        </button>
      </div>
    ))}
    {values.featureBullets.length < 7 && (
      <button
        type="button"
        onClick={() => push('')}
        className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 cursor-pointer mt-1 w-fit bg-transparent border-none p-0"
      >
        <AddIcon style={{ fontSize: '16px' }} />
        Add Bullet
        <span className="text-gray-400 font-normal ml-0.5">
          ({values.featureBullets.length} of 7 max)
        </span>
      </button>
    )}
  </div>
);

export const BackLink = ({ label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer mb-5 hover:text-gray-900 bg-transparent border-none p-0"
  >
    <span className="text-base">‹</span>
    {label}
  </button>
);

export const PageHeading = ({ title, subtitle }) => (
  <div className="mb-7">
    <h1 className="text-xl font-bold text-gray-900 mb-1">{title}</h1>
    <p className="text-xs text-gray-500">{subtitle}</p>
  </div>
);

export const FormFooter = ({ onCancel, submitting, onPublish, publishLabel, onDraft }) => (
  <div className="flex items-center justify-between pt-2 pb-10">
    <button
      type="button"
      onClick={onCancel}
      className="bg-transparent border-none text-xs font-medium text-gray-500 cursor-pointer hover:text-gray-900 p-0"
    >
      Cancel
    </button>
    <div className="flex gap-2.5">
      <LoadingButton
        loading={submitting}
        variant="outlined"
        type="button"
        onClick={onDraft}
        sx={{
          fontSize: '13px', fontWeight: 600, borderRadius: '8px',
          borderColor: '#d1d5db', color: '#374151', textTransform: 'none', px: '20px',
          '&:hover': { borderColor: '#9ca3af', background: '#f9fafb' },
        }}
      >
        Save as Draft
      </LoadingButton>
      <LoadingButton
        loading={submitting}
        variant="contained"
        type="button"
        onClick={onPublish}
        sx={{
          fontSize: '13px', fontWeight: 600, borderRadius: '8px',
          background: '#2563eb', textTransform: 'none', px: '20px',
          '&:hover': { background: '#1d4ed8' },
        }}
      >
        {publishLabel || 'Publish Plan'}
      </LoadingButton>
    </div>
  </div>
);

export const SearchBox = ({ value, onChange, placeholder }) => (
  <div className="flex items-center gap-2 border border-gray-300 rounded-lg px-3 py-2 bg-white">
    <Search style={{ fontSize: '16px', color: '#9ca3af', flexShrink: 0 }} />
    <input
      placeholder={placeholder || 'Search...'}
      value={value}
      onChange={onChange}
      className="border-none outline-none text-xs w-full text-gray-900 bg-transparent placeholder-gray-400"
    />
  </div>
);

export const CoverImageUpload = ({ existingUrl, coverImage, preview, onFileChange, onRemove, error }) => {
  const fileRef = useRef(null);
  const displaySrc = preview || existingUrl || null;

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) onFileChange({ currentTarget: { files: [file] } });
  };

  return (
    <div>
      {!displaySrc ? (
        <div
          role="button"
          tabIndex={0}
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => fileRef.current?.click()}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileRef.current?.click(); }}
          className="border-2 border-dashed border-gray-200 rounded-xl py-10 flex flex-col items-center gap-2 cursor-pointer hover:border-blue-400 hover:bg-blue-50/20 transition-all duration-150"
        >
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <polyline points="21 15 16 10 5 21" />
          </svg>
          <p className="text-sm font-medium text-gray-500">Click to upload or drag &amp; drop</p>
          <p className="text-xs text-gray-400">JPG, PNG, BMP · Min 1200 × 400 px · Max 100 MB</p>
        </div>
      ) : (
        <div className="relative rounded-xl overflow-hidden border border-gray-200 bg-gray-50">
          <img
            src={displaySrc}
            alt="Cover preview"
            className="w-full object-cover"
            style={{ maxHeight: '160px' }}
          />
          <button
            type="button"
            onClick={onRemove}
            className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center text-xs transition-colors cursor-pointer border-none"
          >
            ✕
          </button>
          {coverImage && (
            <div className="absolute bottom-0 left-0 right-0 bg-black/40 px-3 py-1.5">
              <p className="text-xs text-white truncate">{coverImage.name}</p>
            </div>
          )}
          {!coverImage && existingUrl && (
            <div className="absolute bottom-0 left-0 right-0 bg-black/40 px-3 py-1.5 flex items-center justify-between">
              <p className="text-xs text-white">Existing image</p>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="text-xs text-blue-200 hover:text-white border-none bg-transparent cursor-pointer"
              >
                Replace
              </button>
            </div>
          )}
        </div>
      )}
      <input
        ref={fileRef}
        type="file"
        hidden
        accept="image/jpeg,image/png,image/bmp"
        onChange={onFileChange}
      />
      {error && <p className="text-xs text-red-500 mt-1.5">{error}</p>}
    </div>
  );
};